import { ref, type Ref } from 'vue';
import type { Swiper } from 'swiper';
import { useDefaultDataLayer } from '../useDataLayer';
import { useSwiperInteractionDetector } from '../../composables/useSwiperInteractionDetector';

/**
 * Hook for tracking carousel interactions, specifically scroll events (next/previous).
 * This hook provides methods to register carousel scroll events with a data layer,
 * allowing for flexible tracking of 'next' and 'previous' slide movements.
 */

interface UseCarouselTrackingOptions {
  componentName?: string;
  location?: string;
  labelPrev?: string;
  labelNext?: string;
}

export function useCarouselTracking(
  options: UseCarouselTrackingOptions = {},
  swiperInstanceRef: Ref<Swiper | null> = ref(null)
): {
  trackNextSlide: (data?: Record<string, unknown>) => void;
  trackPrevSlide: (data?: Record<string, unknown>) => void;
} {
  const {
    componentName = 'Carousel',
    location = 'body',
    labelPrev = 'click_backwards',
    labelNext = 'click_forwards',
  } = options;
  const { registerInteraction } = useDefaultDataLayer();

  /**
   * A curried function that generates a specific carousel scroll tracking function.
   *
   * The first call defines the `linkText` for the event
   * and allows for `initialAdditionalData` that will be merged with any data
   * provided in the second call.
   * The returned function then takes more specific `additionalData` to register the event.
   */
  const trackCarouselScroll =
    (linkText: string, initialAdditionalData: Record<string, unknown> = {}) =>
    (additionalData: Record<string, unknown> = {}) => {
      registerInteraction({
        event: 'carousel_scroll',
        link_text: linkText,
        component: componentName,
        intent: 'carousel_interaction',
        event_location: location,
        ...initialAdditionalData,
        ...additionalData,
      });
    };

  /**
   * Tracks a 'next' slide interaction in the carousel.
   */
  const trackNextSlide = trackCarouselScroll(labelNext);

  /**
   * Tracks a 'previous' slide interaction in the carousel.
   */
  const trackPrevSlide = trackCarouselScroll(labelPrev);

  const handleScrollTracked = (direction: 'next' | 'prev') => {
    if (direction === 'next') {
      trackNextSlide();
    } else if (direction === 'prev') {
      trackPrevSlide();
    }
  };

  useSwiperInteractionDetector(swiperInstanceRef, handleScrollTracked);

  return {
    trackNextSlide,
    trackPrevSlide,
  };
}
